import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import { getJobPostingFee, refundPayment } from "@endpoints/payment.service";
import { getPaymentMethods } from "@endpoints/stripe.service";
import PaymentCard from "@pages/CreateJobPage2/components/PaymentCard";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const refundMethods = [
  {
    label: "Refund to original payment method",
    desc: "Your refund will be credited back to the card used for this job post. Estimated time: 5–7 business days",
    value: 1,
  },
  {
    label: "Refund to Comtracked Wallet",
    desc: "Get instant credit in your Comtracked virtual account. Use it for future job postings or services.",
    value: 2,
  },
];

const RefundSummary = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { state: jobDetails } = useLocation();
  const [jobPostingFee, setJobPostingFee] = useState(0);
  const [refundMethod, setRefundMethod] = useState<number | undefined>();
  const [tempRefundMethod, setTempRefundMethod] = useState<
    number | undefined
  >();
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [cardList, setCardList] = useState([]);
  const [isRefunding, setIsRefunding] = useState(false);

  const isPaymentLoading = false;

  async function fetchJobPostingFee() {
    try {
      const { success, data } = await getJobPostingFee();
      if (success) {
        setJobPostingFee(data?.amount);
      }
    } catch (error) {
      console.error("Error: Posting job posing fee", error);
    }
  }
  const fetchCards = async () => {
    await getPaymentMethods("card")
      .then((response) => {
        if (response.success) setCardList(response.data);
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }));
  };

  const handleConfirm = async () => {
    if (!refundMethod) {
      enqueueSnackbar("Please select the refund method", { variant: "error" });
      return;
    }
    if (!selectedPaymentDetails) {
      enqueueSnackbar("Please select the payment details", {
        variant: "error",
      });
      return;
    }
    try {
      setIsRefunding(true)
      const { success } = await refundPayment({
        jobId: jobDetails?._id,
      });
      if (success) {
        navigate('/referral/refunds')
        enqueueSnackbar("Amount refunded successfully", { variant: "success" });
      }
    } catch (error) {
      console.error("Error: refunding the amount.", error);
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsRefunding(false);
    }
  };

  useEffect(() => {
    fetchCards();
    fetchJobPostingFee();
  }, []);

  console.log("job details", jobDetails);

  return (
    <div className="flex flex-col py-4 min-h-0">
      <button
        onClick={() => navigate("/requirements")}
        className="flex items-center self-start gap-1"
      >
        <ChevronLeftIcon className={"w-4 h-4"} fill="#4d4d4d" />
        <label className="text-xs font-medium text-charcoal cursor-pointer">
          Back to listing
        </label>
      </button>
      <div className="flex flex-1 min-h-0">
        {/* Left section */}
        <div className="w-1/2 flex flex-col flex-1 min-h-0 border-r px-6 py-4 overflow-y-scroll no-scrollbar">
          <span className="font-bold text-sm pb-3">Refund summary</span>
          <div className="flex flex-col py-4 border-y gap-2">
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">
                Reference:
              </span>
              <span className="font-medium text-xs text-charcoal">
                {jobDetails?.refNo}
              </span>
            </div>
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">Title:</span>
              <span className="font-medium text-xs text-charcoal">
                {jobDetails?.title}
              </span>
            </div>
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">
                Job closed date:
              </span>
              <span className="font-medium text-xs text-charcoal">
                Immediately
              </span>
            </div>
          </div>

          {/* Refund method */}
          <div className="flex flex-col mt-6">
            <span className="font-bold text-sm pb-1.5 border-b mb-3">
              Refund method
            </span>
            {!refundMethod ? (
              <>
                <div className="flex flex-col gap-6">
                  {refundMethods?.map((refund) => {
                    const isSelected = refund?.value === tempRefundMethod;
                    return (
                      <button
                        onClick={() => setTempRefundMethod(refund?.value)}
                        className="flex gap-3"
                      >
                        <div className="h-5 w-5 rounded-full border-2 border-primary flex justify-center items-center">
                          {isSelected ? (
                            <div className="h-3 w-3 rounded-full bg-primary" />
                          ) : null}
                        </div>
                        <div className="flex flex-col flex-1 gap-2.5">
                          <span className="font-medium text-left text-xs">
                            {refund?.label}
                          </span>
                          <span className="font-medium text-left text-xs text-light-black">
                            {refund?.desc}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setRefundMethod(tempRefundMethod)}
                  className="flex items-center justify-center gap-2 border rounded-md mt-6 py-2 font-semibold text-sm text-white w-max px-8 bg-primary hover:bg-black"
                >
                  Select
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setRefundMethod(undefined)}
                    className="text-blue-500 font-medium text-xs hover:text-blue-900"
                  >
                    Change refund method
                  </button>
                  <button className="flex justify-center items-center font-semibold text-xs border-primary border rounded-full py-2 px-4 hover:bg-gray-50">
                    Add a new card
                  </button>
                </div>
                {/* Payment method */}
                <div className="flex flex-col mt-3">
                  <span className="font-medium text-xs pb-3.5">
                    Select the card you’d like to receive the refund to
                  </span>
                  <div className="flex flex-col gap-3">
                    {cardList?.map((card) => {
                      const isSelected =
                        selectedPaymentDetails?.id === card?.id;
                      return (
                        <PaymentCard
                          key={card?._id}
                          data={card}
                          isSelected={isSelected}
                          onClick={() => {
                            if (selectedPaymentDetails?.id === card?.id) {
                              setSelectedPaymentDetails(null);
                            } else {
                              setSelectedPaymentDetails(card);
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {/* Right Section */}
        <div className="w-1/2 flex flex-col flex-1 min-h-0 px-6 py-4 overflow-y-scroll no-scrollbar">
          <span className="font-bold text-sm pb-3">Refund Amount Details</span>

          <div className="flex flex-col gap-2 py-4 border-y">
            <div className="flex items-center font-medium text-light-black text-xs">
              <span className="w-36">Referral Fee</span>
              <div className="flex items-center gap-3">
                £
                <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                  {jobPostingFee}
                </div>
              </div>
            </div>
            <div className="flex items-center font-medium text-light-black text-xs">
              <span className="w-36">Transaction Fee</span>
              <div className="flex items-center gap-3">
                £
                <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                  {jobPostingFee}
                </div>
              </div>
            </div>
          </div>
          {/* Total */}
          <div className="flex flex-col gap-2 py-4 border-b">
            <div className="flex items-center font-medium text-light-black text-xs">
              <span className="w-36">Total</span>
              <div className="flex items-center gap-3">
                £
                <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                  {jobPostingFee}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleConfirm}
            disabled={isRefunding}
            type="button"
            className="flex items-center justify-center gap-3 bg-primary rounded-md mt-6 text-white font-semibold py-3 hover:bg-black text-sm"
          >
            Confirm {isRefunding ? <LoadingAnimation /> : null}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundSummary;
